import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { AccountModel } from '../../../../../typings/api/skymap/rest/v0/account';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { textUtils } from '../../../utils/text/text';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useToaster } from '../../state/toaster-state';
import { Button } from '../button/button';
import { DataTable, DataTableColumns, DataTableSortType } from '../data-table/data-table';
import { InfoBox } from '../info-box/info-box';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';

type AccountsRow = {
  id: string;
  name: string;
  accountKey: string;
  actions: React.ReactNode;
};

const ACCOUNTS_QUERY_KEY = ['accounts'];

const fetchAccounts = async (): Promise<AccountModel[]> => {
  const service = SkyMapAxiosServiceFactory.instance.createAccountServiceV0();
  const response = await service.fetchAccounts({ query: {} });
  return response.data?.accounts ?? [];
};

const createAccount = async (name: string) => {
  const service = SkyMapAxiosServiceFactory.instance.createAccountServiceV0();
  const response = await service.createAccount({ body: { name } });
  return response.data;
};

const normalizeName = (value: string) => value.trim().toLowerCase();

const AccountsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const createFormId = React.useId();
  const [accountName, setAccountName] = React.useState('');
  const [recentAccount, setRecentAccount] = React.useState<{ name: string; accountKey: string }>();
  const { showToast } = useToaster();

  const { handleError, clearErrors, buildErrorList } = useErrorHandling();

  const accountsQuery = useQuery({
    queryKey: ACCOUNTS_QUERY_KEY,
    queryFn: fetchAccounts,
    retry: false,
  });

  React.useEffect(() => {
    if (accountsQuery.error) {
      handleError(accountsQuery.error, { retryCallback: accountsQuery.refetch });
    } else {
      clearErrors();
    }
  }, [accountsQuery.error, accountsQuery.refetch, clearErrors, handleError]);

  const handleCopyKey = React.useCallback(
    (accountKey: string) => {
      textUtils.copyTextToClipboard(accountKey);
      showToast({
        type: 'success',
        title: t('toaster.title.success', { ns: 'common' }),
        message: t('accountsPage.toaster.copySuccess', { ns: 'components' }),
      });
    },
    [showToast, t],
  );

  const mutation = useMutation({
    mutationFn: async (name: string) => createAccount(name),
    onSuccess: async (data, name) => {
      setRecentAccount({ name, accountKey: data.accountKey });
      setAccountName('');
      clearErrors();
      showToast({
        type: 'success',
        title: t('toaster.title.success', { ns: 'common' }),
        message: t('accountsPage.toaster.createSuccess', { ns: 'components', name }),
      });
      await queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
    },
    onError: (error) => {
      handleError(error);
    },
  });

  const copyLabel = t('accountsPage.table.copy', {
    ns: 'components',
  });

  const columns = React.useMemo<DataTableColumns<AccountsRow>>(
    () => ({
      name: {
        title: t('name', { ns: 'common' }),
        unfilterable: true,
        sortableBy: { type: DataTableSortType.STRING },
      },
      accountKey: {
        title: t('accountsPage.table.accountKey', {
          ns: 'components',
        }),
        unfilterable: true,
        monospaced: 'montserrat',
      },
      actions: {
        title: '',
        unfilterable: true,
        width: '160px',
        alignment: 'right',
        padding: '0',
      },
    }),
    [t],
  );

  const rows = React.useMemo<AccountsRow[]>(() => {
    const accountList = accountsQuery.data ?? [];
    return accountList.map((account) => ({
      id: account.id,
      name: account.name,
      accountKey: account.accountKey,
      actions: (
        <Button
          aria-label={copyLabel}
          leftIcon={{ icon: 'copy' }}
          padding="0.35em 0.75em"
          variant="text"
          onClick={() => handleCopyKey(account.accountKey)}
        >
          {copyLabel}
        </Button>
      ) as React.ReactNode,
    }));
  }, [accountsQuery.data, copyLabel, handleCopyKey]);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedName = accountName.trim();
      if (!trimmedName || mutation.status === 'pending') {
        return;
      }

      const accountList = accountsQuery.data ?? [];
      const hasDuplicate = accountList.some(
        (account) => normalizeName(account.name) === normalizeName(trimmedName),
      );

      if (hasDuplicate) {
        showToast({
          type: 'error',
          title: t('toaster.title.error', { ns: 'common' }),
          message: t('accountsPage.create.duplicate', {
            ns: 'components',
          }),
        });
        return;
      }

      mutation.mutate(trimmedName);
    },
    [accountName, accountsQuery.data, mutation, showToast, t],
  );

  const errorList = buildErrorList();

  return (
    <Page spacing={2.5}>
      {errorList}

      <Section spacing={1.5}>
        <SectionHeader spacing={0.5}>
          <SectionTitle>
            {t('accountsPage.create.heading', {
              ns: 'components',
            })}
          </SectionTitle>
          <SectionDescription>
            {t('accountsPage.create.description', {
              ns: 'components',
            })}
          </SectionDescription>
        </SectionHeader>

        {recentAccount && (
          <InfoBox color="green" leftIcon={{ icon: ['fas', 'circle-check'] }}>
            <Stack spacing={0.75}>
              <InfoTitle>
                {t('accountsPage.created.title', {
                  ns: 'components',
                })}
              </InfoTitle>
              <InfoDescription>
                {t('accountsPage.created.description', {
                  ns: 'components',
                  name: recentAccount.name,
                })}
              </InfoDescription>
              <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
                <AccountKeyCode className="notranslate">{recentAccount.accountKey}</AccountKeyCode>
                <Button
                  color="primary"
                  leftIcon={{ icon: 'copy' }}
                  noWrap={true}
                  type="button"
                  variant="contained"
                  onClick={() => handleCopyKey(recentAccount.accountKey)}
                >
                  {t('accountsPage.created.copy', {
                    ns: 'components',
                  })}
                </Button>
              </Stack>
            </Stack>
          </InfoBox>
        )}

        <CreateForm id={createFormId} onSubmit={handleSubmit}>
          <FormField spacing={0.75}>
            <LabelledContainer
              required={true}
              text={t('accountsPage.create.nameLabel', {
                ns: 'components',
              })}
            >
              {(formElementId) => (
                <TextBox
                  id={formElementId()}
                  placeholder={t('accountsPage.create.placeholder', {
                    ns: 'components',
                  })}
                  value={accountName}
                  onChange={(event) => setAccountName(event.target.value)}
                />
              )}
            </LabelledContainer>
          </FormField>

          <Button
            color="primary"
            disabled={accountName.trim().length === 0}
            loading={mutation.status === 'pending'}
            type="submit"
            variant="contained"
            width="fit-content"
          >
            {t('accountsPage.create.button', {
              ns: 'components',
            })}
          </Button>
        </CreateForm>
      </Section>

      <Section spacing={1.5}>
        <SectionHeader spacing={0.5}>
          <SectionTitle>
            {t('accountsPage.table.title', {
              ns: 'components',
            })}
          </SectionTitle>
        </SectionHeader>

        <DataTable
          columns={columns}
          items={rows}
          loading={accountsQuery.isLoading || accountsQuery.isFetching}
          pageSize={20}
          tableStyle={{
            rowCell: {
              fontSize: '14px',
            },
            headerCell: {
              fontSize: '14px',
            },
          }}
        />

        {!accountsQuery.isLoading && rows.length === 0 && (
          <InfoBox color="yellow" leftIcon={{ icon: ['fas', 'circle-info'] }} topMargin={true}>
            {t('accountsPage.table.empty', {
              ns: 'components',
            })}
          </InfoBox>
        )}
      </Section>
    </Page>
  );
};

const Page = styled(Stack)`
  min-height: 60vh;
`;

const Section = styled(Stack)`
  background: #fff;
  padding: 24px;
  box-shadow:
    0px 4px 8px rgba(55, 66, 74, 0.12),
    0px 1px 4px rgba(55, 66, 74, 0.08);
`;

const SectionHeader = styled(Stack)`
  max-width: 560px;

  p {
    color: ${(props) => props.theme.color.gray.dark};
  }
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: ${(props) => props.theme.color.gray.darkest};
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${(props) => props.theme.color.gray.dark};
`;

const InfoTitle = styled.strong`
  font-size: 1rem;
  color: ${(props) => props.theme.color.gray.darkest};
`;

const InfoDescription = styled.span`
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.95rem;
`;

const AccountKeyCode = styled.code`
  font-family: 'Roboto Mono', monospace;
  font-size: 0.95rem;
  padding: 0.65em 0.9em;
  border-radius: 8px;
  background: ${(props) => props.theme.color.gray.lightest};
  border: 1px solid ${(props) => props.theme.color.gray.light};
  word-break: break-all;
`;

const CreateForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1.25em;
`;

const FormField = styled(Stack)`
  flex: 1 1 260px;
`;

export { AccountsPage };
